/**
 * Catalogs specific to a single order.
 *
 * Delivery statuses are NOT here — they live in `utils/catalogs` because the
 * deliveries table paints badges with the same list.
 */

/** Order types, as persisted by the backend. */
export const ORDER_TYPE_OPTIONS = [
  { value: 'click_collect', label: 'Click & Collect' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'delivery_fulfillment', label: 'Delivery Fulfillment' },
  { value: 'pay_collect', label: 'Pay & Collect' },
  { value: 'pick_up_fulfillment', label: 'Pick Up Fulfillment' },
  { value: 'pick_up', label: 'Pick Up' },
  { value: 'delivery_return', label: 'Devolución' },
];

/** Where the order came from. */
export const ORDER_SOURCE_OPTIONS = [
  { value: 'excel', label: 'Excel' },
  { value: 'single_form_upload', label: 'Single Form Upload' },
  { value: 'shopify', label: 'Shopify' },
  { value: 'meli', label: 'Mercado Libre' },
  { value: 'meli_flex', label: 'Mercado Libre Flex' },
  { value: 'meli_places', label: 'Mercado Libre Places' },
  { value: 'meli_collect', label: 'Mercado Libre Colecta' },
  { value: 'meli_full', label: 'Mercado Libre Full' },
  { value: 'woocommerce', label: 'Woocommerce' },
  { value: 'bootic', label: 'Bootic' },
  { value: 'vtex', label: 'VTex' },
  { value: 'prestashop', label: 'Prestashop' },
  { value: 'api', label: 'API' },
  { value: 'not_selected', label: 'Origen desconocido' },
];

/**
 * The shipping methods an operator may pick when editing an order.
 *
 * Deliberately a subset of `SHIPPING_METHOD_OPTIONS`: the marketplace methods
 * (`meli*`) and the courier pickups are assigned by the integration, never by
 * hand, so offering them here would let an operator break the link with the
 * channel that created the order.
 */
export const EDITABLE_SHIPPING_METHODS = [
  { value: 'express', label: 'Envío Express' },
  { value: 'same_day', label: 'Envío Same day' },
  { value: 'regional', label: 'Envío Regional' },
  { value: 'rm_rural', label: 'Envío RM Rural' },
  { value: 'pod', label: 'Envío a Punto de Despacho' },
];

/** Order types whose detail renders the shipping block (address, courier, tracking). */
export const SHIPPABLE_ORDER_TYPES = [
  'delivery',
  'delivery_fulfillment',
  'pick_up',
  'pick_up_fulfillment',
  'delivery_return',
];

/**
 * Order types that are collected at a counter instead of shipped.
 *
 * Their timeline runs on order-level statuses (`in_preparation` → …) rather
 * than on delivery statuses.
 */
export const COLLECT_ORDER_TYPES = ['click_collect', 'pay_collect'];

/**
 * Status labels for the collect flow.
 *
 * `pending_assignment` reads differently here than in the shipping flow — the
 * customer is told the parcel is ready to pick up, not that it is ready to be
 * dispatched.
 */
export const COLLECT_STATUS_LABELS = {
  in_preparation: 'En preparación',
  pending_assignment: 'Listo para retirar',
  completed: 'Entregado',
};

/**
 * Order-level statuses that show up in a shipping timeline alongside the
 * delivery statuses of `DELIVERY_STATUSES`.
 */
export const ORDER_STATUS_LABELS = {
  in_preparation: 'En preparación',
  pending_assignment: 'Preparado para envío',
  pick_up_ready: 'Listo para retirar',
};

/**
 * The order must be in preparation for its shipping data to still be editable:
 * once a courier has the parcel, changing the address changes nothing.
 */
export const EDITABLE_MIXED_STATUS = 'in_preparation';

/** Order types whose shipping data can be edited at all. */
export const EDITABLE_ORDER_TYPES = ['delivery', 'delivery_fulfillment'];
